import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { ReactSVG } from "react-svg";
import { webURL } from "../../../services/API";
import { workflowItems } from "../../workflow";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { BarChart3, FileCheck, Zap, X, Activity, TrendingUp, XCircle } from "lucide-react";
import styled from "styled-components";

type BillingSummaryItem = {
   blockName: string;
   blockAction: string;
   credentialSource: 'dragify' | 'user';
   executionCount: number;
   inputTokens: number;
   outputTokens: number;
   embeddingTokens: number;
   baseCredits: number;
   tokenCredits: number;
   marginCredits: number;
   roundingCredits?: number;
   totalCredits: number;
};

export function ProjectLog({ projectId, nodes }: { projectId: string, nodes: any[] }) {
   const settingsIsRun = useSelector((state: RootState) => state.settings.data.isRunForProcess);
   const isWorkflowRun = useSelector((state: RootState) => state.settings.data.isWorkflowRun);
   const [isToggle, setIsToggle] = React.useState(false);
   const [viewMode, setViewMode] = React.useState<'billing' | 'legacy'>('legacy');
   const [items, setItems] = React.useState<{ name: string, credits: number, count: number, isActive: boolean }[]>([]);
   const [billingSummary, setBillingSummary] = React.useState<BillingSummaryItem[]>([]);
   const [billingConfig, setBillingConfig] = React.useState<{ marginPercent: number, creditValueEgp: number } | null>(null);
   const totalRequests = React.useMemo(() => items.reduce((total, item) => total + item.count, 0), [items]);
   const totalCredits = React.useMemo(() => items.reduce((total, item) => total + item.credits, 0), [items]);
   const billingRequests = React.useMemo(() => billingSummary.reduce((total, item) => total + Number(item.executionCount || 0), 0), [billingSummary]);
   const billingCredits = React.useMemo(() => billingSummary.reduce((total, item) => total + Number(item.totalCredits || 0), 0), [billingSummary]);
   const visibleRequests = viewMode === 'billing' && billingSummary.length ? billingRequests : totalRequests;
   const visibleCredits = viewMode === 'billing' && billingSummary.length ? billingCredits : totalCredits;
   const activeIndexes = React.useMemo(() => new Set(nodes.map((node: any) => node.data.index)), [nodes]);

   const fallbackNodeRows = React.useMemo(() => {
      return (nodes || [])
         .filter((node: any) => Boolean(node?.data?.index))
         .map((node: any) => ({
            name: String(node.data.index),
            credits: 0,
            count: 0,
            isActive: true,
         }));
   }, [nodes]);

   // ---
   React.useEffect(() => {
      if (!projectId) {
         setItems(fallbackNodeRows);
         setBillingSummary([]);
         setBillingConfig(null);
         return;
      }

      (async function () {
         const [legacyResult, billingResult] = await Promise.allSettled([
            axios.get(`${webURL}/process?projectId=${projectId}`),
            axios.get(`${webURL}/process/billing-usage?projectId=${projectId}&limit=150`)
         ]);

            const legacyData = legacyResult.status === 'fulfilled' && Array.isArray(legacyResult.value?.data)
               ? legacyResult.value.data
               : [];

         const processRows = legacyData
            .map((item: any) => ({
               ...item,
               isActive: nodes.some((node: any) => node.data.index === item.name)
            }));

         if (processRows.length) {
            const byName = new Map(processRows.map((row: any) => [row.name, row]));
            const merged = [...processRows];

            for (const fallback of fallbackNodeRows) {
               if (!byName.has(fallback.name)) merged.push(fallback);
            }
            setItems(merged);
         } else {
            setItems(fallbackNodeRows);
         }

         if (billingResult.status === 'fulfilled') {
            const billingData = billingResult.value?.data || {};
            setBillingSummary(Array.isArray(billingData.summary) ? billingData.summary : []);
            setBillingConfig(billingData.config || null);
         } else {
            setBillingSummary([]);
            setBillingConfig(null);
         }
      })();
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [projectId, settingsIsRun, isWorkflowRun]);

   React.useEffect(() => {
      if (!billingSummary.length) {
         setViewMode('legacy');
      }
   }, [billingSummary.length]);

   return (
      <div>
         <div className="boxLog processReport" onClick={ _=> setIsToggle(true) }>
            <div className="icon"><FileCheck size="16px" /></div>
            <div className="info">
               <span>Process Report</span>
               <div>
                  <span><BarChart3 size="12px" color="rgb(59, 130, 246)"/>{ totalRequests } Total</span>
                  <span><Zap size="12px" color="rgb(34, 197, 94)" />{ totalCredits } Credits</span>
               </div>
            </div>
         </div>
         { isToggle ? <Popup onClick={ _=> setIsToggle(false) }>
            <div className="direct" onClick={ e => e.stopPropagation() }>
               <div className="close" onClick={ _=> setIsToggle(false) }>
                  <X size={ 19 } />
               </div>
               <div className="iconTitle">
                  <div><Activity size={ 20 } /></div>
                  <h3>Process Report <span>For current blocks</span></h3>
               </div>
               <div className="cards">
                  <div className="card">
                     <div>
                        <span className="title">Total Credits</span>
                        <span className="num">{ visibleCredits }</span>
                     </div>
                     <Zap size={ 30 } />
                  </div>
                  <div className="card">
                     <div>
                        <span className="title">Total Processes</span>
                        <span className="num">{ visibleRequests }</span>
                     </div>
                     <TrendingUp size={ 30 } />
                  </div>
               </div>
               <div className="viewSwitch">
                  <button
                     className={viewMode === 'billing' ? 'active' : ''}
                     onClick={_ => setViewMode('billing')}
                  >
                     Transparent AI Billing
                  </button>
                  <button
                     className={viewMode === 'legacy' ? 'active' : ''}
                     onClick={_ => setViewMode('legacy')}
                  >
                     Legacy Process Counts
                  </button>
               </div>
               <div className="table">
                  {viewMode === 'billing' && billingSummary.length ? <>
                     <div className="billingHint">
                        Formula: Charged Total = Base + Token + Margin + Rounding. Margin applies only to Dragify-managed credentials.
                        {billingConfig ? ` Margin ${billingConfig.marginPercent}% | Credit Value ${billingConfig.creditValueEgp} EGP` : ''}
                     </div>
                     <div className="billingGrid billingHead">
                        <span>Block</span>
                        <span>Credential</span>
                        <span>Tokens (In/Out/Emb)</span>
                        <span>Base</span>
                        <span>Token</span>
                        <span>Margin</span>
                        <span>Rounding</span>
                        <span>Total</span>
                     </div>
                     <div className="scroll">{
                        billingSummary.length ? billingSummary.map((item) => {
                           const icon: any = workflowItems.find(i => item.blockName.includes(i.action) || item.blockAction === i.action)?.icon;
                           const isActive = activeIndexes.has(item.blockName);

                           return (
                              <div key={`${item.blockName}-${item.credentialSource}`} className="billingGrid billingRow">
                                 <span className="blockCell">
                                    <span className="icon">
                                       { icon ? (typeof icon === 'string' ? <ReactSVG className="otherSvg" src={ icon } /> : <FontAwesomeIcon icon={ icon } />) : null }
                                    </span>
                                    <div>
                                       <span className="name">{ item.blockName }</span>
                                       { isActive ? <div className="status">
                                          <Activity size={ 15 } />
                                          <span>active</span>
                                       </div> :
                                       <div className="status remove">
                                          <XCircle size={ 15 } />
                                          <span>deleted</span>
                                       </div>}
                                    </div>
                                 </span>
                                 <span className={item.credentialSource === 'user' ? 'userKey' : 'dragifyKey'}>{ item.credentialSource }</span>
                                 <span>{ `${item.inputTokens}/${item.outputTokens}/${item.embeddingTokens}` }</span>
                                 <span>{ Number(item.baseCredits || 0).toFixed(2) }</span>
                                 <span>{ Number(item.tokenCredits || 0).toFixed(3) }</span>
                                 <span>{ Number(item.marginCredits || 0).toFixed(3) }</span>
                                 <span>{ Number(item.roundingCredits || 0).toFixed(3) }</span>
                                 <span>{ Number(item.totalCredits || 0).toFixed(2) }</span>
                              </div>
                           );
                        }) : <div className="empty">No AI billing rows yet.</div>
                     }</div>
                  </> : <>
                     <ul className="titles">
                        <li>Block Name</li>
                        <li>Credits</li>
                        <li>Processes</li>
                     </ul>
                     <div className="scroll">{
                        items.length ? items.map(item => {
                           const icon: any = workflowItems.find(i => item.name.includes(i.action))?.icon;

                           return (
                              <ul key={ item.name } className="row">
                                 <li>
                                    <span className="icon">
                                       { icon ? (typeof icon === 'string' ? <ReactSVG className="otherSvg" src={ icon } /> : <FontAwesomeIcon icon={ icon } />) : null }
                                    </span>
                                    <div>
                                       <span className="name">{ item.name }</span>
                                       { item.isActive ? <div className="status">
                                          <Activity size={ 15 } />
                                          <span>active</span>
                                       </div> :
                                       <div className="status remove">
                                          <XCircle size={ 15 } />
                                          <span>deleted</span>
                                       </div>}
                                    </div>
                                 </li>
                                 <li>{ item.credits }</li>
                                 <li>{ item.count }</li>
                              </ul>
                           );
                        }) : <div className="empty">There are no blocks in the workflow!</div>
                     }</div>
                  </>}
               </div>
            </div>
         </Popup> : null }
      </div>
   );
}

const Popup = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background-color: rgba(30, 39, 46, 0.65);
   z-index: 999;

   .direct {
      display: flex;
      flex-direction: column;
      position: relative;
      background-color: #fff;
      padding: 30px;
      border-radius: 5px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
      width: 850px;
      height: 550px;
      overflow: hidden;
      box-sizing: border-box;

      .close {
         position: absolute;
         top: 20px;
         right: 20px;
         opacity: 0.65;
         font-size: 20px;
         padding: 5px 10px;
         transition: all .2s ease-in-out;
         cursor: pointer;

         &:hover {
            opacity: 0.8;
         }
      }
      .iconTitle {
         display: flex;
         align-items: center;
         margin-bottom: 30px;

         > div {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            background-image: linear-gradient(to right, #3b82f6, #06b6d4);
            color: #fff;
            border-radius: 5px;
            margin-right: 10px;
         }
         h3 {
            display: flex;
            align-items: center;
            font-weight: 400;

            span {
               opacity: 0.8;
               font-weight: 500;
               font-size: 13px;
               margin-left: 7.5px;
               background-color: rgb(219, 234, 254);
               color: rgb(30, 64, 175);
               border: 1px solid rgb(191, 219, 254);
               border-radius: 25px;
               padding: 3px 15px;
            }
         }
      }
      .icon {
         display: flex;
         align-items: center;
         justify-content: center;
         width: 40px !important;
         height: 40px;
         background-image: linear-gradient(to bottom right, #f1f5f9, #f1f5f9);
         box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.2);
         margin-right: 10px;
         border-radius: 10px;

         .otherSvg {
            width: 18px !important;
            height: 18px !important;

            svg {
               width: 100% !important;
               height: 100% !important;
            }
         }
      }
      .cards {
         display: flex;
         justify-content: space-between;
         margin-bottom: 30px;
         
         .card { 
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: calc(50% - 7.5px);
            box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
            padding: 15px;
            border-radius: 10px;

            &:first-of-type {
               border: 1px solid rgb(191, 219, 254);
               background-image: linear-gradient(to bottom right, #eff6ff, #ecfeff);

               span:first-of-type {
                  color: rgb(37, 99, 235);
               }
               span:last-of-type {
                  color: rgb(30, 64, 175);
               }
               svg {
                  color: rgb(97, 145, 249);
               }
            }
            &:last-of-type {
               border: 1px solid rgb(187, 247, 208);
               background-image: linear-gradient(to bottom right, #f0fdf4, #ecfdf5);
               
               span:first-of-type {
                  color: rgb(22, 163, 74);
               }
               span:last-of-type {
                  color: rgb(22, 101, 52);
               }
               svg {
                  color: rgb(54, 206, 110);
               }
            }
            > div {
               span {
                  display: block;

                  &:first-of-type {
                     font-size: 15px;
                     margin-bottom: 5px;
                  }
                  &:last-of-type {
                     font-weight: 500;
                  }
               }
            }
         }
      }
      .viewSwitch {
         display: flex;
         gap: 10px;
         margin-bottom: 15px;

         button {
            border: 1px solid rgb(203, 213, 225);
            background-color: rgb(248, 250, 252);
            color: rgb(51, 65, 85);
            border-radius: 8px;
            padding: 8px 12px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;

            &.active {
               border-color: rgb(125, 211, 252);
               background-color: rgb(239, 246, 255);
               color: rgb(30, 64, 175);
            }

            &:disabled {
               opacity: 0.45;
               cursor: not-allowed;
            }
         }
      }
      .table {
         display: flex;
         flex-direction: column;
         height: calc(100% - 36px - 30px - 76.6px - 30px);
         box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
         border: 1px solid rgb(226, 232, 240);
         border-radius: 10px;
         overflow: hidden;
         min-width: 0;

         .billingHint {
            font-size: 12px;
            color: rgb(71, 85, 105);
            padding: 10px 15px;
            background-image: linear-gradient(to right, #f8fafc, #f0fdf4);
            border-bottom: 1px solid rgb(226, 232, 240);
         }

         .billingGrid {
            display: grid;
            grid-template-columns: 2.55fr 0.85fr 1.35fr 0.65fr 0.65fr 0.65fr 0.7fr 0.65fr;
            align-items: center;
            column-gap: 10px;
            padding: 0 15px;
            min-height: 58px;
            box-sizing: border-box;

            > span {
               font-size: 13px;
               min-width: 0;
               overflow-wrap: anywhere;
            }

            .blockCell {
               display: flex;
               align-items: center;
            }

            &.billingHead {
               font-weight: 600;
               color: rgb(30, 41, 59);
               background-image: linear-gradient(to right, #f8fafc, #eff6ff);
               border-bottom: 1px solid rgb(226, 232, 240);
               min-height: 44px;
            }

            &.billingRow {
               &:not(:last-of-type) {
                  border-bottom: 1px solid rgb(226, 232, 240);
               }

               .dragifyKey,
               .userKey {
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  border-radius: 30px;
                  padding: 2px 10px;
                  font-size: 11px;
                  font-weight: 600;
                  text-transform: uppercase;
               }

               .dragifyKey {
                  background-color: rgb(219, 234, 254);
                  border: 1px solid rgb(191, 219, 254);
                  color: rgb(30, 64, 175);
               }

               .userKey {
                  background-color: rgb(220, 252, 231);
                  border: 1px solid rgb(187, 247, 208);
                  color: rgb(22, 101, 52);
               }
            }
         }

         ul {
            display: flex;
            opacity: 0.8;

            li {
               display: flex;
               align-items: center;
               height: 35px;
               padding: 0 20px;
               box-sizing: border-box;
               min-width: 0;
               overflow-wrap: anywhere;

               &:not(:first-of-type) {
                  width: 25%;
               }
               &:first-of-type {
                  width: 50%;
               }
            }
         }
         .titles {
            display: flex;
            align-items: center;
            background-image: linear-gradient(to right, #f8fafc, #eff6ff);
            border-bottom: 1px solid rgb(226, 232, 240);
            font-size: 16px;
            font-weight: 500;
            border-radius: 10px 10px 0 0;
            height: 80px;
         }
         .status {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0 10px;
            height: 22px;
            background-color: rgb(219, 234, 254);
            color: rgb(30, 64, 175);
            border: 1px solid rgb(191, 219, 254);
            border-radius: 25px;
            margin-top: 7.5px;
            font-size: 13px;
            font-weight: 500;

            &.remove {
               background-color: rgb(254, 226, 226);
               border: 1px solid rgb(254, 202, 202);
               color: rgb(153, 27, 27);
            }
            span {
               margin-left: 5px;
            }
         }
         .row {
            display: flex;
            align-items: center;
            // opacity: 0.5;
            height: 75px;
            font-size: 17px;
            
            &:not(:last-of-type) {
               border-bottom: 1px solid rgb(226, 232, 240);
            }
            li > div {
               display: flex;
               flex-direction: column;
               align-items: flex-start;
            }
         }
         .scroll {
            overflow: auto;
            height: 409.4px;
            min-width: 0;

            &::-webkit-scrollbar {
               width: 0;
            }
         }
      }
   }
   .empty {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      opacity: 0.3;
   }
`;